import { headerHeightMobile } from '@pushpay/shell';
import { multiply, add, createUseStyles } from '@pushpay/styles';
import { GivingOldTheme, isGivingOldTheme } from '@pushpay/theming';
import { navigationWidth, overlayWidthMobile, headerHeight, navigationWrapperSizeMobile, footerHeight, headerHoverColor, headerFocusColor } from '../../common';
const useStyles = createUseStyles(theme => {
  const multiplyGutter = index => multiply(theme.SPACING.GUTTER, index);

  const transition = prop => `${prop} 250ms linear`;

  const activeBorderWidth = '5px';
  const isGivingOld = theme.colors['product-color'] === GivingOldTheme.colors['product-color'];
  return {
    root: {
      position: 'fixed',
      top: headerHeight,
      left: 0,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      color: theme.colors['color-text-heading'],
      borderRight: `1px solid ${theme.colors['color-border-surface']}`,
      backgroundColor: theme.colors['color-background-surface'],
      width: navigationWidth,
      height: `calc(100vh - ${headerHeight})`,
      fontSize: theme.TYPOGRAPHY.BASE_SIZE,
      zIndex: theme.ELEVATION.LAYOUT.Z_INDEX,
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        position: 'static',
        height: `calc(100vh - ${headerHeightMobile})`,
        width: overlayWidthMobile,
        maxWidth: '75vw',
        overflowY: 'auto'
      },
      [theme.mediaQuery({
        mediaType: 'PRINT'
      })]: {
        display: 'none'
      }
    },
    buttonOverride: {
      textAlign: 'left'
    },
    icon: {
      fontSize: theme.SPACING.LARGE_ICON_SIZE,
      marginRight: multiplyGutter(2)
    },
    linklist: {
      listStyle: 'none',
      width: '100%',
      padding: 0,
      marginBottom: 0,
      flexGrow: 1,
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        marginBottom: multiplyGutter(4)
      }
    },
    linkContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    topLevelItemLinkContainer: {
      borderLeft: `solid ${activeBorderWidth} transparent`,
      transition: transition('background-color'),
      '&:hover, &:focus': {
        backgroundColor: theme.colors['palette-grey-light-300']
      }
    },
    basicLink: {
      flex: 1,
      font: theme.TYPOGRAPHY.BODY_2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      textDecoration: 'none',
      color: 'inherit',
      transition: transition('color'),
      padding: `${multiplyGutter(1)} 0 ${multiplyGutter(1)} ${multiplyGutter(6)}`,
      cursor: 'pointer',
      outline: 'none',
      '&:hover, &:focus': {
        color: theme.colors['product-color'],
        textDecoration: 'none'
      }
    },
    topLevelItem: {
      margin: 0,
      width: '100%'
    },
    topLevelLink: {
      font: theme.TYPOGRAPHY.BODY_1,
      padding: multiplyGutter(2),
      transition: transition('color'),
      '&:hover, &:focus': {
        color: 'inherit'
      },
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        fontSize: `${add(theme.TYPOGRAPHY.BASE_SIZE, 2)}`
      }
    },
    subList: {
      listStyle: 'none',
      borderLeft: `solid ${activeBorderWidth} transparent`,
      padding: 0
    },
    subItem: {
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        fontSize: theme.TYPOGRAPHY.BASE_SIZE
      }
    },
    navFooter: {
      borderTop: `solid 1px ${theme.colors['color-border-surface']}`,
      borderLeft: `solid ${activeBorderWidth} transparent`,
      display: 'flex',
      alignItems: 'center',
      transition: transition('background-color'),
      minHeight: footerHeight,
      '&:hover, &:focus-within': {
        backgroundColor: theme.colors['palette-grey-light-300']
      }
    },
    feedbackLink: {
      display: 'flex',
      alignItems: 'center',
      flex: '1 1 auto',
      textDecoration: 'none',
      font: theme.TYPOGRAPHY.BODY_2,
      color: theme.colors['color-text-default'],
      padding: '14px',
      '&:hover, &:focus': {
        textDecoration: 'none'
      }
    },
    activeItem: {
      color: theme.colors['product-color'],
      backgroundColor: theme.colors['palette-grey-light-300'],
      borderColor: theme.colors['product-color'],
      '&:hover, &:focus': {
        color: theme.colors['product-color']
      }
    },
    activeSubItem: {
      color: theme.colors['product-color'],
      fontWeight: 600
    },
    mobileWrapper: {
      position: 'fixed',
      top: 0,
      left: 0,
      height: navigationWrapperSizeMobile,
      width: navigationWrapperSizeMobile,
      backgroundColor: isGivingOld ? theme.colors['product-color'] : undefined,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: theme.ELEVATION.LAYOUT.Z_INDEX + 1,
      borderBottom: isGivingOldTheme(theme) ? undefined : `1px solid ${theme.colors['color-border-surface']}`
    },
    mobileButton: {
      width: '40px',
      height: '40px',
      padding: theme.SPACING.XSMALL,
      fontSize: '24px',
      display: 'flex',
      zIndex: theme.ELEVATION.LAYOUT.Z_INDEX + 2,
      color: isGivingOld ? theme.colors['color-text-inverse'] : theme.colors['color-text-default'],
      borderRadius: theme.SHAPE.CIRCULAR,
      '&:hover': {
        backgroundColor: isGivingOld ? headerHoverColor : theme.colors['palette-grey-light-300']
      },
      '&:focus': {
        backgroundColor: isGivingOld ? headerFocusColor : theme.colors['palette-grey-light-300']
      }
    },
    mobileButtonIcon: {
      height: '16px',
      width: '24px',
      position: 'relative',
      borderTop: `2px solid currentColor`,
      borderBottom: `2px solid currentColor`,
      '&:after': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        height: '2px',
        width: '100%',
        backgroundColor: 'currentColor'
      }
    },
    mobileButtonLoading: {
      color: theme.colors['palette-red']
    },
    warningLink: {
      display: 'flex',
      alignItems: 'center'
    },
    warningIcon: {
      fontSize: theme.SPACING.LARGE_ICON_SIZE,
      color: theme.colors['color-text-error'],
      marginRight: multiply(theme.SPACING.GUTTER, 2)
    },
    drawerPosition: {
      top: headerHeightMobile,
      overflow: 'hidden'
    },
    logoWrapper: {
      display: 'flex',
      flex: 'none',
      flexFlow: 'column',
      alignItems: 'center',
      marginBottom: multiplyGutter(4),
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        marginBottom: 0
      }
    },
    orgLogo: {
      margin: `${multiplyGutter(4)} 0`,
      width: '5em',
      height: '5em'
    },
    orgName: {
      font: theme.TYPOGRAPHY.BODY_2,
      color: theme.colors['color-text-default'],
      textAlign: 'center'
    },
    orgSwitcher: {
      borderBottom: `solid 1px ${theme.colors['color-border-surface']}`
    }
  };
});
export default useStyles;